import React, { useState } from 'react';
import { useTranslation } from '../../config/TranslationProvider';
import { useAppConfiguration } from '../../AppConfiguration';
import { usePlaygroundsFilter } from '../../util/playgrounds';
import { useEvents, useLocations } from '../../api/hooks';
import MapMultiple from './MapMultiple';
import EventMarker from './EventMarker';
import LocationMarker from './LocationMarker';
import LoadOtherResourcesSwitch from '../filter/fields/LoadOtherResourcesSwitch';

const MapMultipleEvents = ({initialEvents, filter}) => {
    const [boundingBox, setBoundingBox] = useState(null);
    const [events, setEvents] = useState(initialEvents);
    const {data: newEvents, isLoading} = useEvents(
        {
            ...filter,
            bbox: boundingBox,
        },
        500,
        0,
        {enabled: !!boundingBox},
    );

    // Don't lose old events while updating them for the new bounding box
    if (newEvents && newEvents !== events) {
        setEvents(newEvents);
    }

    const {mapConfiguration: {allowLoadingOtherResources}} = useAppConfiguration();
    const [showLocations, setShowLocations] = useState(false);

    const {fulltext, tags, approved_tags} = filter;
    const locationsFilter = {fulltext, tags, approved_tags, isPOI: true, bbox: boundingBox};
    const [locations, setLocations] = useState(null);
    const {data: newLocations} = useLocations(locationsFilter, 500, 0, {
        enabled: showLocations && !!boundingBox
    });

    // Don't lose old locations while updating them for the new bounding box
    if (newLocations && newLocations !== locations) {
        setLocations(newLocations);
    }

    const [showPlaygrounds, setShowPlaygrounds] = useState(false);
    const [playgrounds, setPlaygrounds] = useState(null);
    const {data: newPlaygrounds} = useLocations(usePlaygroundsFilter(locationsFilter), 500, 0, {
        enabled: showPlaygrounds && !!boundingBox
    });

    // Don't lose old locations while updating them for the new bounding box
    if (newPlaygrounds && newPlaygrounds !== playgrounds) {
        setPlaygrounds(newPlaygrounds);
    }

    const items = [];
    for (const event of events.data) {
        // Skip events without dates. This can happen if the event was rescheduled and the built filter query somehow
        // does match the event but the actual event dates don't.
        if (!event.event_dates || event.event_dates.length <= 0) {
            continue;
        }

        // Since we get results like 13.5151515,514 we have to split it to make sure we always get valid coordinates
        event.location.lat = event.location.lat.split(',')[0];
        event.location.lng = event.location.lng.split(',')[0];

        items.push({
            lat: event.location.lat,
            lng: event.location.lng,
            marker: <EventMarker key={event.id} event={event} />,
        });
    }

    if (showLocations && locations) {
        for (const location of locations.data) {
            // Since we get results like 13.5151515,514 we have to split it to make sure we always get valid coordinates
            location.lat = location.lat.split(',')[0];
            location.lng = location.lng.split(',')[0];

            items.push({
                lat: location.lat,
                lng: location.lng,
                marker: <LocationMarker key={location.id} location={location} popup={true} />,
            });
        }
    }

    if (showPlaygrounds && playgrounds) {
        for (const playground of playgrounds.data) {
            // Since we get results like 13.5151515,514 we have to split it to make sure we always get valid coordinates
            playground.lat = playground.lat.split(',')[0];
            playground.lng = playground.lng.split(',')[0];

            items.push({
                lat: playground.lat,
                lng: playground.lng,
                marker: <LocationMarker key={playground.id} location={playground} popup={true} />,
            });
        }
    }

    return (
        <MapMultiple
            items={items}
            info={useTranslation('map_popup')}
            loading={isLoading}
            boundingBox={boundingBox}
            setBoundingBox={setBoundingBox}
        >
            {allowLoadingOtherResources && <>
                <LoadOtherResourcesSwitch
                    showOtherResources={showLocations}
                    setShowOtherResources={enabled => {
                        setShowLocations(enabled);

                        if (enabled) {
                            setShowPlaygrounds(false);
                        }
                    }}
                    translationKey="add_locations"
                    row={1}
                />
                <LoadOtherResourcesSwitch
                    showOtherResources={showPlaygrounds}
                    setShowOtherResources={enabled => {
                        setShowPlaygrounds(enabled);

                        if (enabled) {
                            setShowLocations(false);
                        }
                    }}
                    translationKey="show_playgrounds"
                    row={2}
                />
            </>}
        </MapMultiple>
    );
};

export default MapMultipleEvents;
